/**
 * テロップの入場 / 退場アニメーションの正本。
 * docs/features/video-editor-capability-gaps-plan.md §4.2-B1。
 *
 * 同じ意味・同じ数式の実装が 3 か所にある (どれかを変えたら必ず 3 つとも変える):
 * - ここ (image-generator FE: プレビュー・UI・書き出しの注記)
 * - video-editor リポジトリの src/schema/captionAnimation.ts (Cloud Run の MP4 レンダラー)
 * - backend/services/video_edit/caption_extensions.py (AI 提案の正規化)
 *
 * ## 意味
 * - `animationIn` / `animationOut`: 種類。省略・`none` = アニメーションなし (既定。既存テロップの
 *   見た目を変えない)
 * - `animationInSec` / `animationOutSec`: 秒数 (0.1〜1.0。省略時は
 *   DEFAULT_CAPTION_ANIMATION_IN_SEC / OUT_SEC)
 *
 * ## 表示
 * テロップの表示区間 (startSec〜endSec) の先頭 `inSec` と末尾 `outSec` で、進行から
 * 不透明度 / 縦移動 / 拡大 / 表示文字数を補間する (evaluateCaptionAnimation)。表示尺が短いときは
 * 入場 + 退場が重ならないよう、合計が表示尺の CAPTION_ANIMATION_MAX_DURATION_RATIO を超えないところまで
 * 同じ比率で縮める (resolveCaptionAnimationTiming。この 1 か所だけがクランプ規則を持つ)。
 *
 * 縦移動は**出力の高さに対する %** で返す。呼び出し側 (EditVideo の DesignedCaption) は
 * テロップ全体を包む画面いっぱいの層に `translateY(%) scale()` を掛けるので、横書き・縦書き・
 * 帯・箱のどの形でも同じ式で効く (拡大の原点はテロップの基準点)。
 *
 * ## 書き出し
 * Premiere / FCPXML には対応物が無いので**出さない** (§0-2)。ZIP の README と保存時のトーストで
 * 「アニメーションは書き出しに含まれません」と注記する (export/xmlZip.ts)。
 */
import { easeMotion } from './motion';

/** 入場アニメーションの種類。`none` = なし (既定)。 */
export const CAPTION_ANIMATIONS_IN = ['none', 'fade', 'slide-up', 'slide-down', 'pop', 'typewriter'] as const;
export type CaptionAnimationIn = (typeof CAPTION_ANIMATIONS_IN)[number];

/** 退場アニメーションの種類。入場より少ない (退場のタイプライターは意味が薄いので持たない)。 */
export const CAPTION_ANIMATIONS_OUT = ['none', 'fade', 'slide-down', 'pop'] as const;
export type CaptionAnimationOut = (typeof CAPTION_ANIMATIONS_OUT)[number];

/** 秒数の既定値 (種類だけ指定して秒数を省略したとき)。 */
export const DEFAULT_CAPTION_ANIMATION_IN_SEC = 0.3;
export const DEFAULT_CAPTION_ANIMATION_OUT_SEC = 0.2;
/** 秒数の許容範囲。 */
export const CAPTION_ANIMATION_SEC_MIN = 0.1;
export const CAPTION_ANIMATION_SEC_MAX = 1.0;
/** 入場 + 退場が表示尺に占めてよい割合の上限 (これを超えたら同じ比率で縮める)。 */
export const CAPTION_ANIMATION_MAX_DURATION_RATIO = 0.8;
/** スライドの移動量 (出力の高さに対する %)。 */
export const CAPTION_ANIMATION_SLIDE_PERCENT = 3;
/** ポップの開始 / 終了の拡大率 (1 = 等倍)。 */
export const CAPTION_ANIMATION_POP_SCALE = 0.7;

/** 表示名 (UI のセレクタ。AI へのプロンプトにも同じ語を使う)。 */
export const CAPTION_ANIMATION_IN_LABELS: Record<CaptionAnimationIn, string> = {
  none: 'なし',
  fade: 'フェード',
  'slide-up': '下から出す',
  'slide-down': '上から出す',
  pop: 'ポップ',
  typewriter: 'タイプライター',
};
export const CAPTION_ANIMATION_OUT_LABELS: Record<CaptionAnimationOut, string> = {
  none: 'なし',
  fade: 'フェード',
  'slide-down': '下へ消す',
  pop: 'ポップ',
};

/**
 * このモジュールが読む design の部分集合。project.ts への循環参照を避けるため構造型で受ける
 * (CaptionDesign はこの形を満たす)。
 */
export interface CaptionAnimationInput {
  animationIn?: string | null;
  animationOut?: string | null;
  animationInSec?: number | null;
  animationOutSec?: number | null;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}

function round2(value: number): number {
  // half-up (captionCatalog の roundToStep と同じ式。step 0.01)
  return Math.floor(value * 100 + 0.5) / 100;
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** 入場の種類 (不正・省略・'none' はすべて 'none')。 */
export function resolveCaptionAnimationIn(design: CaptionAnimationInput | null | undefined): CaptionAnimationIn {
  const value = design?.animationIn;
  return (CAPTION_ANIMATIONS_IN as readonly string[]).includes(value as string)
    ? (value as CaptionAnimationIn)
    : 'none';
}

/** 退場の種類 (不正・省略・'none' はすべて 'none')。 */
export function resolveCaptionAnimationOut(design: CaptionAnimationInput | null | undefined): CaptionAnimationOut {
  const value = design?.animationOut;
  return (CAPTION_ANIMATIONS_OUT as readonly string[]).includes(value as string)
    ? (value as CaptionAnimationOut)
    : 'none';
}

/** 入場 / 退場のどちらかが設定されているか (書き出しの注記・要約で使う)。 */
export function hasCaptionAnimation(design: CaptionAnimationInput | null | undefined): boolean {
  return resolveCaptionAnimationIn(design) !== 'none' || resolveCaptionAnimationOut(design) !== 'none';
}

export interface CaptionAnimationTiming {
  /** 入場に使う秒数 (0 = 入場なし)。 */
  inSec: number;
  /** 退場に使う秒数 (0 = 退場なし)。 */
  outSec: number;
}

/**
 * 表示尺 durationSec に対して実際に使う入場 / 退場の秒数。**クランプ規則はここ 1 か所**にまとめる:
 * 合計が表示尺の CAPTION_ANIMATION_MAX_DURATION_RATIO を超えたら、同じ比率で両方を縮める
 * (短いテロップで入場と退場が重なって「出た瞬間に消える」状態にしない)。
 */
export function resolveCaptionAnimationTiming(
  design: CaptionAnimationInput | null | undefined,
  durationSec: number,
): CaptionAnimationTiming {
  const duration = isFiniteNumber(durationSec) ? Math.max(0, durationSec) : 0;
  const animIn = resolveCaptionAnimationIn(design);
  const animOut = resolveCaptionAnimationOut(design);
  const wantedIn =
    animIn === 'none'
      ? 0
      : clamp(
          isFiniteNumber(design?.animationInSec) ? (design!.animationInSec as number) : DEFAULT_CAPTION_ANIMATION_IN_SEC,
          CAPTION_ANIMATION_SEC_MIN,
          CAPTION_ANIMATION_SEC_MAX,
        );
  const wantedOut =
    animOut === 'none'
      ? 0
      : clamp(
          isFiniteNumber(design?.animationOutSec)
            ? (design!.animationOutSec as number)
            : DEFAULT_CAPTION_ANIMATION_OUT_SEC,
          CAPTION_ANIMATION_SEC_MIN,
          CAPTION_ANIMATION_SEC_MAX,
        );
  const total = wantedIn + wantedOut;
  const budget = duration * CAPTION_ANIMATION_MAX_DURATION_RATIO;
  if (total <= 0 || total <= budget) return { inSec: round4(wantedIn), outSec: round4(wantedOut) };
  const ratio = budget / total;
  return { inSec: round4(wantedIn * ratio), outSec: round4(wantedOut * ratio) };
}

export interface CaptionAnimationState {
  /** 0〜1。 */
  opacity: number;
  /** 出力の高さに対する % (正 = 下へ)。 */
  translateYPercent: number;
  /** 拡大率 (テロップの基準点が原点)。 */
  scale: number;
  /** タイプライターで表示する文字の割合 (0〜1)。1 = 全文表示。 */
  revealRatio: number;
}

const NEUTRAL: CaptionAnimationState = { opacity: 1, translateYPercent: 0, scale: 1, revealRatio: 1 };

/** アニメーションが効いていない (= 既定の見た目) 状態か。描画側の分岐に使う。 */
export function isNeutralCaptionAnimation(state: CaptionAnimationState): boolean {
  return state.opacity === 1 && state.translateYPercent === 0 && state.scale === 1 && state.revealRatio === 1;
}

/**
 * テロップの表示開始からの経過秒 tSec における見た目の状態。
 *
 * @param design animationIn / animationOut / 秒数を持つ design
 * @param tSec テロップの表示開始からの経過秒 (シーン内のローカル秒 - caption.startSec)
 * @param durationSec テロップの表示尺 (endSec - startSec)
 */
export function evaluateCaptionAnimation(
  design: CaptionAnimationInput | null | undefined,
  tSec: number,
  durationSec: number,
): CaptionAnimationState {
  const duration = isFiniteNumber(durationSec) ? Math.max(0, durationSec) : 0;
  if (duration <= 0) return NEUTRAL;
  const t = clamp(isFiniteNumber(tSec) ? tSec : 0, 0, duration);
  const { inSec, outSec } = resolveCaptionAnimationTiming(design, duration);
  const animIn = resolveCaptionAnimationIn(design);
  const animOut = resolveCaptionAnimationOut(design);

  if (animIn !== 'none' && inSec > 0 && t < inSec) {
    const p = clamp(t / inSec, 0, 1);
    // 出はじめを速く、着地を穏やかに (ease-out)。タイプライターだけは等速で文字を送る
    const e = easeMotion('ease-out', p);
    switch (animIn) {
      case 'fade':
        return { ...NEUTRAL, opacity: round4(e) };
      case 'slide-up':
        return {
          ...NEUTRAL,
          opacity: round4(e),
          translateYPercent: round4(CAPTION_ANIMATION_SLIDE_PERCENT * (1 - e)),
        };
      case 'slide-down':
        return {
          ...NEUTRAL,
          opacity: round4(e),
          translateYPercent: round4(-CAPTION_ANIMATION_SLIDE_PERCENT * (1 - e)),
        };
      case 'pop':
        return {
          ...NEUTRAL,
          opacity: round4(e),
          scale: round4(CAPTION_ANIMATION_POP_SCALE + (1 - CAPTION_ANIMATION_POP_SCALE) * e),
        };
      case 'typewriter':
        return { ...NEUTRAL, revealRatio: round4(p) };
      default:
        return NEUTRAL;
    }
  }

  if (animOut !== 'none' && outSec > 0 && t > duration - outSec) {
    const q = clamp((duration - t) / outSec, 0, 1);
    const e = easeMotion('ease-out', q);
    switch (animOut) {
      case 'fade':
        return { ...NEUTRAL, opacity: round4(e) };
      case 'slide-down':
        return {
          ...NEUTRAL,
          opacity: round4(e),
          translateYPercent: round4(CAPTION_ANIMATION_SLIDE_PERCENT * (1 - e)),
        };
      case 'pop':
        return {
          ...NEUTRAL,
          opacity: round4(e),
          scale: round4(CAPTION_ANIMATION_POP_SCALE + (1 - CAPTION_ANIMATION_POP_SCALE) * e),
        };
      default:
        return NEUTRAL;
    }
  }

  return NEUTRAL;
}

/**
 * タイプライターで表示する部分文字列。改行も 1 文字として数えるので、行は上から順に現れる
 * (未表示の文字を透明にするのではなく切り詰めるため、帯 / 箱は文字の増加に合わせて広がる)。
 * サロゲートペア・結合文字を割らないよう Array.from で分割する。
 */
export function typewriterVisibleText(text: string, revealRatio: number): string {
  if (revealRatio >= 1) return text;
  const chars = Array.from(text);
  const count = Math.ceil(chars.length * clamp(revealRatio, 0, 1));
  return chars.slice(0, count).join('');
}

/**
 * 外部入力 (AI の提案・古い保存データ) のアニメーション指定を保存形へ正規化する。
 * **保存値 = 実効値**にするため、`none` / 不正値はキーごと落とし、秒数は種類があるときだけ残す。
 * backend の caption_extensions.normalize_caption_animation と同じ規則。
 */
export function normalizeCaptionAnimation(design: CaptionAnimationInput | null | undefined): {
  animationIn?: CaptionAnimationIn;
  animationOut?: CaptionAnimationOut;
  animationInSec?: number;
  animationOutSec?: number;
} {
  const result: {
    animationIn?: CaptionAnimationIn;
    animationOut?: CaptionAnimationOut;
    animationInSec?: number;
    animationOutSec?: number;
  } = {};
  const animIn = resolveCaptionAnimationIn(design);
  if (animIn !== 'none') {
    result.animationIn = animIn;
    if (isFiniteNumber(design?.animationInSec)) {
      result.animationInSec = round2(
        clamp(design!.animationInSec as number, CAPTION_ANIMATION_SEC_MIN, CAPTION_ANIMATION_SEC_MAX),
      );
    }
  }
  const animOut = resolveCaptionAnimationOut(design);
  if (animOut !== 'none') {
    result.animationOut = animOut;
    if (isFiniteNumber(design?.animationOutSec)) {
      result.animationOutSec = round2(
        clamp(design!.animationOutSec as number, CAPTION_ANIMATION_SEC_MIN, CAPTION_ANIMATION_SEC_MAX),
      );
    }
  }
  return result;
}

/** UI / 要約の表示名 (「下から出す → フェード」のような 1 行)。アニメーション無しは「なし」。 */
export function captionAnimationLabel(design: CaptionAnimationInput | null | undefined): string {
  const animIn = resolveCaptionAnimationIn(design);
  const animOut = resolveCaptionAnimationOut(design);
  if (animIn === 'none' && animOut === 'none') return 'なし';
  const parts: string[] = [];
  if (animIn !== 'none') parts.push(`入 ${CAPTION_ANIMATION_IN_LABELS[animIn]}`);
  if (animOut !== 'none') parts.push(`出 ${CAPTION_ANIMATION_OUT_LABELS[animOut]}`);
  return parts.join(' / ');
}
